/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';

import type { ModalState } from '../../redux/modal/reducers';
import type { CombinedReducers } from '../../redux/reducers';

import ErrorBoundary from '../errorBoundary/ErrorBoundary';
import AuthenticationRequiredModal from './authenticationRequiredModal/AuthenticationRequiredModal';
import AvenueModal from './avenueModal/AvenueModal';
import CardModal from './cardModal/CardModal';
import ConfirmationModal from './confirmationModal/ConfirmationModal';
import EulaModal from './eulaModal/EulaModal';
import ExternalContentModal from './externalContentModal/ExternalContentModal';
import GameProfileSelectModal from './gameProfileSelectModal/GameProfileSelectModal';
import KeyboardShortcutsModal from './keyboardShortcutsModal/KeyboardShortcutsModal';
import LegalNoticeModal from './legalNoticeModal/LegalNoticeModal';
import MessageModal from './messageModal/MessageModal';
import {
  AUTHENTICATION_REQUIRED_MODAL,
  AVENUE_MODAL,
  CARD_MODAL,
  CONFIRMATION_MODAL,
  EULA_MODAL,
  EXTERNAL_CONTENT_MODAL,
  GAME_PROFILE_SELECT_MODAL,
  KEYBOARD_SHORTCUTS_MODAL,
  LEGAL_NOTICE_MODAL,
  MESSAGE_MODAL,
  type MODAL_TYPE,
  NEW_VERSION_MODAL,
  NPVR_HELP_MODAL,
  NPVR_MODAL,
  PARENTAL_CONTROL_MODAL,
  PIN_MODAL,
  PRESENCE_CHECK_MODAL,
  PROFILES_MODAL,
  PROFILE_AVATAR_SELECT_MODAL,
  PROFILE_EDITOR_MODAL,
  PROFILE_SPLASHSCREEN_MODAL,
  SETTINGS_MODAL,
  VOD_PURCHASE_MODAL,
} from './modalTypes';
import NewVersionModal from './newVersionModal/NewVersionModal';
import NpvrHelpModal from './npvrHelpModal/NpvrHelpModal';
import NpvrModal from './npvrModal/NpvrModal';
import ParentalControlModal from './parentalControlModal/ParentalControlModal';
import PinModal from './pinModal/PinModal';
import PresenceCheckModal from './presenceCheckModal/PresenceCheckModal';
import ProfileAvatarSelectModal from './profileAvatarSelectModal/ProfileAvatarSelectModal';
import ProfileEditorModal from './profileEditorModal/ProfileEditorModal';
import ProfilesModal from './profilesModal/ProfilesModal';
import ProfileSplashscreenModal from './profileSplashscreen/ProfileSplashscreenModal';
import SettingsModal from './settingsModal/SettingsModal';
import VodPurchaseModal from './vodPurchaseModal/VodPurchaseModal';

type ReduxModalContainerReducerStateType = {
  +openModals: Array<ModalState>,
};

type ModalContainerPropType = {};

type CompleteModalContainerPropType = {
  ...ModalContainerPropType,
  ...ReduxModalContainerReducerStateType,
};

const MODAL_COMPONENTS: { [MODAL_TYPE]: React.ComponentType<Object> } = {
  [AUTHENTICATION_REQUIRED_MODAL]: AuthenticationRequiredModal,
  [AVENUE_MODAL]: AvenueModal,
  [CARD_MODAL]: CardModal,
  [CONFIRMATION_MODAL]: ConfirmationModal,
  [EULA_MODAL]: EulaModal,
  [EXTERNAL_CONTENT_MODAL]: ExternalContentModal,
  [GAME_PROFILE_SELECT_MODAL]: GameProfileSelectModal,
  [KEYBOARD_SHORTCUTS_MODAL]: KeyboardShortcutsModal,
  [LEGAL_NOTICE_MODAL]: LegalNoticeModal,
  [MESSAGE_MODAL]: MessageModal,
  [NEW_VERSION_MODAL]: NewVersionModal,
  [NPVR_HELP_MODAL]: NpvrHelpModal,
  [NPVR_MODAL]: NpvrModal,
  [PARENTAL_CONTROL_MODAL]: ParentalControlModal,
  [PIN_MODAL]: PinModal,
  [PRESENCE_CHECK_MODAL]: PresenceCheckModal,
  [PROFILE_AVATAR_SELECT_MODAL]: ProfileAvatarSelectModal,
  [PROFILE_EDITOR_MODAL]: ProfileEditorModal,
  [PROFILE_SPLASHSCREEN_MODAL]: ProfileSplashscreenModal,
  [PROFILES_MODAL]: ProfilesModal,
  [SETTINGS_MODAL]: SettingsModal,
  [VOD_PURCHASE_MODAL]: VodPurchaseModal,
};

const ModalContainerView = (props: CompleteModalContainerPropType) => {
  const { openModals } = props;

  if (openModals.length === 0) {
    // No modal open
    return null;
  }

  return openModals.map((modalProps) => {
    const { ariaLabel, modalId, modalType } = modalProps;
    if (!modalType) {
      return null;
    }

    const SpecificModal = MODAL_COMPONENTS[modalType];
    const fullProps = {
      ...modalProps,
      ariaLabel,
      modalId,
    };

    /* eslint-disable react/jsx-props-no-spreading */
    return (
      <ErrorBoundary componentName='Modal' key={modalType}>
        <React.Suspense fallback={null}>
          <SpecificModal {...fullProps} />
        </React.Suspense>
      </ErrorBoundary>
    );
    /* eslint-enable react/jsx-props-no-spreading */
  });
};

const mapStateToProps = (state: CombinedReducers) => {
  return {
    openModals: state.modal.openModals,
  };
};

const ModalContainer: React.ComponentType<ModalContainerPropType> = connect(mapStateToProps)(ModalContainerView);

export default ModalContainer;
